// Crystal Prescription Builder — v5 Vedic-aligned Life Path and Name Number crystals.

// Life Path crystals (Strict Vedic Jyotish — substitute stones for the 9 classical grahas + masters).
const LIFE_PATH_CRYSTAL = {
  1: "Sunstone",          // Vedic substitute for Ruby (Sun)
  2: "Pearl",             // Moon
  3: "Citrine",           // Vedic substitute for Yellow Sapphire (Jupiter)
  4: "Smoky Quartz",      // Vedic substitute for Hessonite Garnet (Rahu)
  5: "Green Aventurine",  // Vedic substitute for Emerald (Mercury)
  6: "Clear Quartz",      // Vedic substitute for Diamond (Venus)
  7: "Tiger Eye",         // Vedic substitute for Cat Eye (Ketu)
  8: "Amethyst",          // Vedic substitute for Blue Sapphire (Saturn)
  9: "Red Jasper",        // Vedic substitute for Red Coral (Mars)
  11: "Moonstone",        // Master Moon
  22: "Amethyst",         // Saturn master
  33: "Citrine",          // Jupiter master
};

// Life Path bead counts: 1 for LP1, 3 for LP7/33, 2 for everyone else.
function lifePathBeadCount(lp) {
  if (lp === 1) return 1;
  if (lp === 7 || lp === 33) return 3;
  return 2;
}

const NAME_NUMBER_CRYSTAL = {
  1: "Sunstone",
  2: "Pearl",
  3: "Yellow Topaz",
  4: "Smoky Quartz",
  5: "Green Aventurine",
  6: "Rose Quartz",
  7: "Tiger Eye",
  8: "Amethyst",
  9: "Garnet",
  11: "Moonstone",
  22: "Amethyst",
};

// Plain-language reasons keyed by Life Path number. First line names the
// traditional Vedic stone this crystal stands in for; second line explains
// what it does in everyday words. See the Quick Glossary for planet basics.
const LIFE_PATH_REASON = {
  1: "The Sun rules your Life Path Number. The traditional Sun stone is Ruby — rare and expensive. Red Jasper does the same job for a lot less: boosts your natural leadership energy, keeps you steady when you're the one setting the pace.",
  2: "The Moon rules your Life Path Number. Moonstone is the classic Moon crystal — soothes your feelings, sharpens that gut sense you already have, and makes your natural talent for peacemaking easier to trust.",
  3: "Jupiter rules your Life Path Number. The traditional Jupiter stone is Yellow Sapphire — rare and expensive. Citrine does the same job for a lot less: opens up your creativity, expression, and that warm 'lucky' feeling around you.",
  4: "Rahu rules your Life Path Number. The traditional Rahu stone is Hessonite Garnet — rare and expensive. Smoky Quartz does the same job for a lot less: keeps you grounded and steady, and shields you when things get chaotic around you.",
  5: "Mercury rules your Life Path Number. The traditional Mercury stone is Emerald — rare and expensive. Green Aventurine does the same job for a lot less: sharpens your thinking, keeps your quick mind quicker, and backs up your love of change.",
  6: "Venus rules your Life Path Number. The traditional Venus stone is Diamond — rare and expensive. Rose Quartz does the same job for a lot less: opens your heart, deepens your natural gift for love and harmony.",
  7: "Ketu rules your Life Path Number. The traditional Ketu stone is Cat's Eye — rare and expensive. Tiger Eye does the same job for a lot less: sharpens your intuition, deepens your quiet inner knowing.",
  8: "Saturn rules your Life Path Number. The traditional Saturn stone is Blue Sapphire — rare and expensive. Amethyst does the same job for a lot less: channels your discipline and ambition into results that actually last.",
  9: "Mars rules your Life Path Number. The traditional Mars stone is Red Coral — rare and expensive. Red Jasper does the same job for a lot less: keeps your fire steady, courage grounded, drive pointed the right way.",
  11: "Master Moon energy for the 11. Moonstone opens up your intuition and the soft, guiding presence you naturally carry.",
  22: "Master Saturn energy for the 22 — the 'builder' of masters. Amethyst channels that big vision into things that actually get built.",
  33: "Master Jupiter energy for the 33 — the 'teacher-healer' of masters. Citrine carries that warm, generous wisdom through you.",
};

// Plain-language reasons keyed by Name Number. Says what people feel when
// they meet you and what the crystal does about it.
const NAME_NUMBER_REASON = {
  1: "Your Name Number carries Sun energy — you show up with natural authority. Red Jasper backs up that leadership vibe with steady physical energy.",
  2: "Your Name Number carries Moon energy — quiet, receptive, easy to open up to. Moonstone deepens that natural warmth and your instinct for what other people need.",
  3: "Your Name Number carries Jupiter energy — warm, expressive, people-like-you-more-than-they-say. Citrine amplifies that natural charm and creative pull.",
  4: "Your Name Number carries Rahu energy — quietly reliable, the person everyone counts on. Smoky Quartz keeps that steadiness from tipping into carrying too much.",
  5: "Your Name Number carries Mercury energy — quick, curious, adaptable. Green Aventurine keeps your mind sharp and your words landing well.",
  6: "Your Name Number carries Venus energy — warm, easy to love, natural at making people feel cared for. Rose Quartz deepens that heart-forward vibe.",
  7: "Your Name Number carries Ketu energy — quiet, deep, a little mysterious. Tiger Eye sharpens that gut feeling you get, that sense of just knowing things.",
  8: "Your Name Number carries Saturn energy — grounded authority. People take you seriously without you trying. Amethyst grounds and steadies that presence.",
  9: "Your Name Number carries Mars energy — warm, brave, quietly protective of others. Red Jasper channels that fire into service, not burnout.",
  11: "Master Moon energy in your name — people feel guided just being near you. Moonstone tunes your intuition higher.",
  22: "Master Saturn energy in your name — the builder-visionary vibration. Amethyst helps you turn big vision into things that actually get done.",
};

const ZODIAC_CRYSTAL = {
  Aries: "Red Jasper", Taurus: "Emerald", Gemini: "Citrine",
  Cancer: "Moonstone", Leo: "Sunstone", Virgo: "Peridot",
  Libra: "Lepidolite", Scorpio: "Obsidian", Sagittarius: "Turquoise",
  Capricorn: "Garnet", Aquarius: "Amethyst", Pisces: "Aquamarine",
};

// Plain-language reason for the zodiac crystal, per sign. Format:
// "You're a <sign>, ruled by <planet>. <stone> carries that same <feeling>."
const ZODIAC_REASON = {
  Aries: "You're an Aries, ruled by Mars. Red Jasper carries that same warm, brave, get-things-going energy.",
  Taurus: "You're a Taurus, ruled by Venus. Emerald carries that same grounded, sensual, love-of-the-good-life energy.",
  Gemini: "You're a Gemini, ruled by Mercury. Citrine carries that same bright, curious, quick-witted energy.",
  Cancer: "You're a Cancer, ruled by the Moon. Moonstone carries that same soft, deeply feeling, nurturing energy.",
  Leo: "You're a Leo, ruled by the Sun. Sunstone carries that same warm, glowing, confident energy — sunshine in stone form.",
  Virgo: "You're a Virgo, ruled by Mercury. Peridot carries that same clear, thoughtful, quietly caring energy.",
  Libra: "You're a Libra, ruled by Venus. Lepidolite carries that same easy, balanced, peace-loving energy.",
  Scorpio: "You're a Scorpio, ruled by Pluto (traditionally Mars). Obsidian carries that same deep, honest, unafraid-of-the-truth energy.",
  Sagittarius: "You're a Sagittarius, ruled by Jupiter. Turquoise carries that same wide, adventurous, wise-and-lucky energy.",
  Capricorn: "You're a Capricorn, ruled by Saturn. Garnet carries that same steady, ambitious, long-game energy.",
  Aquarius: "You're an Aquarius, ruled by Uranus (traditionally Saturn). Amethyst carries that same original, thoughtful, quietly rebellious energy.",
  Pisces: "You're a Pisces, ruled by Neptune (traditionally Jupiter). Aquamarine carries that same dreamy, intuitive, deeply-feeling energy.",
};

export const INTENTIONS = [
  { key: "wealth",      label: "Wealth & Confidence",       icon: "\u{1F4B0}", primary: "Pyrite",          support: "Citrine" },
  { key: "luck",        label: "Luck & Prosperity",         icon: "\u{1F340}", primary: "Green Aventurine", support: "Jade" },
  { key: "health",      label: "Health & Wellbeing",        icon: "\u{1F33F}", primary: "Bloodstone",       support: "Clear Quartz" },
  { key: "stress",      label: "Stress & Protection",       icon: "\u{1F6E1}\uFE0F", primary: "Amethyst",   support: "Blue Lace Agate" },
  { key: "love",        label: "Love & Compassion",         icon: "\u2764\uFE0F", primary: "Rose Quartz",   support: "Rhodonite" },
  { key: "confidence",  label: "Courage & Focus",           icon: "\u{1F525}", primary: "Tiger Eye",        support: "Carnelian" },
  { key: "protection",  label: "Grounding & Clearing",      icon: "\u{1F331}", primary: "Black Tourmaline", support: "Smoky Quartz" },
  { key: "cleansing",   label: "Cleansing & Purification",  icon: "\u{1F4A7}", primary: "Selenite",         support: "Clear Quartz" },
  { key: "wisdom",      label: "Wisdom & Communication",    icon: "\u{1F989}", primary: "Lapis Lazuli",     support: "Sodalite" },
  { key: "psychic",     label: "Intuition & Awakening",     icon: "\u{1F52E}", primary: "Labradorite",      support: "Moonstone" },
];

// Wrist-size-aware bracelet targets (8mm beads).
// small ≈ 152mm, average ≈ 176mm, large ≈ 200mm. Anything outside this map
// (missing, empty, older orders) defaults to "average". `MAX_TARGET` is the
// hard defensive ceiling — buildPrescription must never emit more beads than
// this, even under contrived inputs.
export const WRIST_TARGETS = { small: 19, average: 22, large: 25 };
export const DEFAULT_TARGET = 22;
export const MAX_TARGET = 25;

function targetForWrist(wristSize) {
  const key = (wristSize || "").toString().trim().toLowerCase();
  return WRIST_TARGETS[key] || DEFAULT_TARGET;
}

// Builds prescription crystals (excluding Pancha beads) with sources and reasons
export function buildPrescription({ lifePath, nameNumber, zodiac, intentions, panchaBeads, wristSize }) {
  const panchaCrystalNames = new Set((panchaBeads || []).map((b) => b.crystal));
  // Map crystal -> aggregate info
  const map = new Map();

  const add = (crystal, count, source, reason) => {
    if (!crystal) return;
    if (panchaCrystalNames.has(crystal)) {
      // Already in pancha foundation - still track the source but keep bead count only once
      // Per rules: if same crystal in multiple layers count once with highest bead count.
      // Since Pancha is 1 bead and prescription may be 2-3, we merge and take max.
      const existing = map.get(crystal) || { crystal, count: 0, sources: [], reasons: [], inPancha: true };
      existing.inPancha = true;
      existing.count = Math.max(existing.count, count);
      existing.sources.push(source);
      existing.reasons.push(reason);
      map.set(crystal, existing);
      return;
    }
    const existing = map.get(crystal) || { crystal, count: 0, sources: [], reasons: [], inPancha: false };
    existing.count = Math.max(existing.count, count);
    existing.sources.push(source);
    existing.reasons.push(reason);
    map.set(crystal, existing);
  };

  // Life Path crystal
  const lpCrystal = LIFE_PATH_CRYSTAL[lifePath];
  if (lpCrystal) {
    add(lpCrystal, lifePathBeadCount(lifePath), `Life Path ${lifePath}`,
      LIFE_PATH_REASON[lifePath] || `Your Life Path ${lifePath} crystal anchoring your soul blueprint.`);
  }
  // Name Number crystal
  const nnCrystal = NAME_NUMBER_CRYSTAL[nameNumber];
  if (nnCrystal) {
    add(nnCrystal, 2, `Name Number ${nameNumber}`,
      NAME_NUMBER_REASON[nameNumber] || `Your Name Number ${nameNumber} crystal amplifying how you express yourself.`);
  }
  // Zodiac crystal
  const zCrystal = ZODIAC_CRYSTAL[zodiac];
  if (zCrystal) {
    add(zCrystal, 2, `Zodiac ${zodiac}`,
      ZODIAC_REASON[zodiac] || `Your ${zodiac} sun sign stone.`);
  }
  // Intentions — primary stone does most of the work, support stone reinforces it.
  // The form caps intentions at 3, but we defensively clamp here too so a
  // manually-crafted payload can't blow the bracelet up.
  (intentions || []).slice(0, 3).forEach((intKey) => {
    const intent = INTENTIONS.find((i) => i.key === intKey);
    if (!intent) return;
    add(intent.primary, 2, `Intention: ${intent.label}`,
      `Your main stone for ${intent.label} — it does most of the work.`);
    add(intent.support, 1, `Intention support: ${intent.label}`,
      `Backs up ${intent.primary}, doubling down on the same energy.`);
  });

  // Filter out pancha-only entries from prescription layer (kept separately in pancha)
  const prescription = [];
  map.forEach((v) => {
    if (v.inPancha) return; // already in Pancha Foundation
    prescription.push({
      crystal: v.crystal,
      beadCount: v.count,
      source: v.sources[0],
      reason: v.reasons[0],
    });
  });

  // Wrist-size-aware target. Pancha bead count is always 5.
  const target = targetForWrist(wristSize);
  const panchaBeadsTotal = (panchaBeads || []).length;
  const prescriptionBeadsTotal = prescription.reduce((acc, c) => acc + c.beadCount, 0);
  const remaining = Math.max(0, target - (panchaBeadsTotal + prescriptionBeadsTotal));

  // Top up with Clear Quartz spacers to reach the wrist-size target.
  if (remaining > 0) {
    const cq = prescription.find((c) => c.crystal === "Clear Quartz");
    if (cq) {
      cq.beadCount += remaining;
      cq.source = cq.source + " + Spacers";
    } else if (!Array.from(panchaCrystalNames).includes("Clear Quartz")) {
      prescription.push({
        crystal: "Clear Quartz",
        beadCount: remaining,
        source: "Spacers",
        reason: "Boosts everything else on the bracelet, and also works as the clear spacer beads between the other stones.",
      });
    }
  }

  // Hard safety ceiling — never ship more than MAX_TARGET (25) beads regardless
  // of wrist size or intentions. Trim Clear Quartz spacers first (they carry no
  // personalisation). If personalised beads alone exceed MAX_TARGET, log a
  // warning for manual review rather than silently truncating a customer's
  // Life Path / Name Number / Zodiac / Intention crystals.
  let currentTotal = panchaBeadsTotal + prescription.reduce((a, c) => a + c.beadCount, 0);
  if (currentTotal > MAX_TARGET) {
    let overflow = currentTotal - MAX_TARGET;
    const cq = prescription.find((c) => c.crystal === "Clear Quartz");
    if (cq) {
      const take = Math.min(overflow, cq.beadCount);
      cq.beadCount -= take;
      overflow -= take;
      if (cq.beadCount <= 0) {
        const idx = prescription.indexOf(cq);
        if (idx >= 0) prescription.splice(idx, 1);
      }
    }
    currentTotal = panchaBeadsTotal + prescription.reduce((a, c) => a + c.beadCount, 0);
    if (currentTotal > MAX_TARGET && typeof console !== "undefined") {
      console.warn(
        `[buildPrescription] Personalised bead count ${currentTotal} exceeds MAX_TARGET ${MAX_TARGET} ` +
          `for wristSize="${wristSize}" (target=${target}). Manual review recommended — ` +
          `personalised content NOT truncated.`
      );
    }
  }

  return prescription;
}

// Build bracelet bead layout: alternating pancha and prescription beads
export function buildBraceletLayout(panchaBeads, prescription) {
  const panchaList = panchaBeads.map((b) => ({
    crystal: b.crystal,
    size: "8mm",
    isPancha: true,
    element: b.element,
  }));
  // Expand prescription into individual beads
  const rxBeads = [];
  prescription.forEach((p) => {
    for (let i = 0; i < p.beadCount; i++) {
      rxBeads.push({ crystal: p.crystal, size: "8mm", isPancha: false });
    }
  });
  // Distribute prescription beads between pancha beads
  const groups = panchaList.length; // 5 groups
  const perGroup = Math.ceil(rxBeads.length / groups);
  const layout = [];
  for (let g = 0; g < groups; g++) {
    layout.push(panchaList[g]);
    const slice = rxBeads.slice(g * perGroup, (g + 1) * perGroup);
    slice.forEach((b) => layout.push(b));
  }
  return layout;
}

// List of all 36 crystals for inventory seeding (deduplicated)
export const ALL_CRYSTALS = Array.from(new Set([
  ...Object.values(LIFE_PATH_CRYSTAL),
  ...Object.values(NAME_NUMBER_CRYSTAL),
  ...Object.values(ZODIAC_CRYSTAL),
  ...INTENTIONS.flatMap((i) => [i.primary, i.support]),
  // Pancha sets
  "Red Jasper", "Aquamarine", "Carnelian", "Clear Quartz", "Amethyst",
  "Black Tourmaline", "Moonstone", "Pyrite", "Sodalite", "Selenite",
  "Smoky Quartz", "Blue Lace Agate", "Sunstone", "Lapis Lazuli", "Fluorite",
  "Jade", "Rose Quartz", "Tiger Eye", "Yellow Topaz", "Labradorite",
  "Bloodstone", "Pearl", "Citrine", "Celestite",
])).sort();
